import styled from "@emotion/styled";

import { MDSButton, MDSButtonVariant } from "@/mds/components/buttons/button";
import { MDSDialogSlotName, MDSModalDialog } from "@/mds/components/overlays/dialog";

export interface AppleProactiveUpgradeModalViewProps {
  isOpen: boolean;
  onClose: () => void;
  onSubscribe: () => void;
  onSubscribeLater: () => void;
  onRetryPricing?: () => void;
  canSubscribe: boolean;
  canSubscribeLater: boolean;
  pricingIsUnavailable?: boolean;
  isSubscribing?: boolean;
}

export const AppleProactiveUpgradeModalView = ({
  isOpen,
  onClose,
  onSubscribe,
  onSubscribeLater,
  onRetryPricing,
  canSubscribe,
  canSubscribeLater,
  pricingIsUnavailable = false,
  isSubscribing = false,
}: AppleProactiveUpgradeModalViewProps) => (
  <StyledModalDialog
    isOpen={isOpen}
    handleClose={onClose}
    fluidWidth
    data-apple-proactive-upgrade-modal
  >
    <StyledHeader slot={MDSDialogSlotName.Header}>
      <StyledTitle>Cancel your Apple subscription</StyledTitle>
    </StyledHeader>

    <StyledContent slot={MDSDialogSlotName.Content}>
      <StyledBodyCopy>
        You are currently subscribed to Mem Pro through Apple. Cancel your Apple subscription now,
        or continue to Proactive and cancel it later.
      </StyledBodyCopy>

      <StyledButtonRow>
        {!canSubscribe ? (
          <StyledLaterButton
            variant={MDSButtonVariant.Default}
            onPress={onSubscribeLater}
            isDisabled={!canSubscribeLater || pricingIsUnavailable}
            isLoading={isSubscribing}
          >
            I&apos;m going to do it later
          </StyledLaterButton>
        ) : null}
        <StyledUpgradeButton
          variant={MDSButtonVariant.Contrast}
          onPress={pricingIsUnavailable && onRetryPricing ? onRetryPricing : onSubscribe}
          isDisabled={!canSubscribe}
          isLoading={isSubscribing && canSubscribe}
        >
          {pricingIsUnavailable ? "Retry" : "Upgrade to Proactive"}
        </StyledUpgradeButton>
      </StyledButtonRow>
    </StyledContent>
  </StyledModalDialog>
);

const StyledModalDialog = styled(MDSModalDialog)`
  .mds-dialog {
    box-sizing: border-box;
    width: min(528px, calc(100vw - 32px));
    max-width: calc(100vw - 32px);
    padding: ${({ theme }) => theme.spacing["7"]};
    gap: 0;
    outline: none;
  }

  .mds-dialog [slot="header"] {
    padding-block: 0;
  }

  .mds-dialog button[aria-label="Close"] {
    margin-top: ${({ theme }) => theme.spacing["1"]};
    margin-right: 0;
    border: 0;
  }

  [data-mds-name="dialog-body"] {
    overflow: visible;
    scrollbar-gutter: auto;
  }

  &[data-apple-proactive-upgrade-modal],
  &[data-apple-proactive-upgrade-modal] * {
    letter-spacing: 0;
  }
`;

const StyledHeader = styled.div`
  min-width: 0;
  padding: 0;
`;

const StyledTitle = styled.h1`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.contrast};
  font-size: 28px;
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  line-height: 36px;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["5"]};
  padding-top: ${({ theme }) => theme.spacing["4"]};
`;

const StyledBodyCopy = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: 22px;
`;

const StyledButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing["3"]};
  justify-content: flex-end;
`;

const StyledUpgradeButton = styled(MDSButton)`
  min-width: 171px;
  height: 40px;
  border: 0;
  padding: ${({ theme }) => `${theme.spacing["3"]} ${theme.spacing["4"]}`};
  font-size: 14px;
  line-height: 16px;
`;

const StyledLaterButton = styled(StyledUpgradeButton)`
  min-width: 197px;
`;
